import { memo, type ReactNode } from 'react';
import { cn } from '../../../../lib/utils';
import { renderSummaryCellContent, resolveAlign } from '../column-types';
import { ALIGN_CLASS, DENSITY_CELL, type Density } from '../styles';
import type { DataGridColumn, DataGridSummaryItem } from '../types';

export interface SubtotalRowProps<T> {
  /** Depth in the group tree — drives the left padding of the label cell. */
  level: number;
  /** Path from the root — the last segment is used as the "Sous-total X" label. */
  path: string[];
  rows: T[];
  columns: DataGridColumn<T>[];
  summary: DataGridSummaryItem<T>[];
  density: Density;
  currency: string;
}

/**
 * Subtotal row rendered right after the data rows of an expanded group.
 * Non-summary columns show either the "Sous-total X" italic label (on
 * the first column) or nothing.
 */
function SubtotalRowImpl<T>({
  level,
  path,
  rows,
  columns,
  summary,
  density,
  currency,
}: SubtotalRowProps<T>) {
  const indent = level * 20;
  return (
    <tr className="border-b border-neutral-200 bg-neutral-50/60 font-medium">
      {columns.map((col, colIdx) => {
        const isFirst = colIdx === 0;
        const item = summary.find((s) => s.columnId === col.id);
        return (
          <td
            key={col.id}
            className={cn(
              'align-middle text-dark-500 text-sm',
              DENSITY_CELL[density],
              ALIGN_CLASS[resolveAlign(col)],
              col.cellClassName
            )}
            style={isFirst ? { paddingLeft: `${16 + indent}px` } : undefined}
          >
            {item ? (
              renderSummaryCellContent(col, summary, rows, currency)
            ) : isFirst ? (
              <span className="text-neutral-500 italic">
                Sous-total {path[path.length - 1]}
              </span>
            ) : null}
          </td>
        );
      })}
    </tr>
  );
}

export const SubtotalRow = memo(SubtotalRowImpl) as <T>(
  props: SubtotalRowProps<T>
) => ReactNode;
